import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { OdlInputEvent } from '@odilo/ui-components';
import type { Competency } from '../../../models/metadata-response';
import type { QuestionAnswerType } from '../../../models/quiz';
import type { AiTextSource, QuizMode } from '../../../services/quiz/quiz';
import { QuizService } from '../../../services/quiz/quiz';
import { SkeletonComponent } from '../../skeleton/skeleton';
import { CompetencyPickerComponent } from '../competency-picker/competency-picker';

const MIN_AI_QUESTIONS = 1;
const MAX_AI_QUESTIONS = 15;
const MIN_AI_ANSWERS = 2;
const MAX_AI_ANSWERS = 6;
const DEFAULT_AI_QUESTIONS = 1;
const DEFAULT_AI_ANSWERS = 3;
const QUIZ_MODE_SETTINGS_ROWS = 2;
const QUESTION_MODE_SETTINGS_ROWS = 1;

export interface QuizAiGenerateEvent {
  source: AiTextSource;
  questionsNumber: number;
  numberOfAnswers: number;
  competencyIds: string[];
  answerType: QuestionAnswerType;
}

@Component({
  selector: 'nr-quiz-ai-panel',
  imports: [TranslatePipe, SkeletonComponent, CompetencyPickerComponent],
  templateUrl: './quiz-ai-panel.html',
  styleUrl: './quiz-ai-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class QuizAiPanelComponent {
  private readonly quizService = inject(QuizService);

  protected readonly MIN_AI_QUESTIONS = MIN_AI_QUESTIONS;

  protected readonly MAX_AI_QUESTIONS = MAX_AI_QUESTIONS;

  protected readonly MIN_AI_ANSWERS = MIN_AI_ANSWERS;

  protected readonly MAX_AI_ANSWERS = MAX_AI_ANSWERS;

  readonly seedText = input.required<string>();
  readonly competencies = input<Competency[]>([]);
  readonly generating = input(false);
  readonly mode = input<QuizMode>('quiz');

  readonly generate = output<QuizAiGenerateEvent>();
  readonly skip = output();

  readonly source = signal<AiTextSource>('visible');
  readonly questionsNumber = signal(DEFAULT_AI_QUESTIONS);
  readonly numberOfAnswers = signal(DEFAULT_AI_ANSWERS);
  readonly selectedCompetencies = signal<Competency[]>([]);
  readonly answerType = signal<QuestionAnswerType>('multipleChoice');

  readonly loadingQuestionIndexes = computed(() =>
    Array.from({ length: this.mode() === 'question' ? 1 : this.questionsNumber() }, (_, i) => i),
  );

  readonly loadingAnswerIndexes = computed(() =>
    Array.from({ length: this.numberOfAnswers() }, (_, i) => i),
  );

  readonly loadingOpenEnded = computed(
    () => this.mode() === 'question' && this.answerType() === 'openEnded',
  );

  readonly loadingSettingsRowIndexes = computed(() => {
    const skillsRow = this.competencies().length > 0 ? 1 : 0;
    const responseTimeRow = 1;
    const modeRows =
      this.mode() === 'question' ? QUESTION_MODE_SETTINGS_ROWS : QUIZ_MODE_SETTINGS_ROWS;
    return Array.from({ length: skillsRow + responseTimeRow + modeRows }, (_, i) => i);
  });

  hasContent(): boolean {
    return this.quizService.hasGenerationContent(this.source(), this.seedText());
  }

  onSourceChange(event: OdlInputEvent): void {
    const { value } = event.detail;
    if (value === 'visible' || value === 'chapter') this.source.set(value);
  }

  onAnswerTypeChange(event: OdlInputEvent): void {
    const { value } = event.detail;
    if (value === 'openEnded' || value === 'multipleChoice') this.answerType.set(value);
  }

  incrementQuestionsNumber(): void {
    this.questionsNumber.update((current) => Math.min(MAX_AI_QUESTIONS, current + 1));
  }

  decrementQuestionsNumber(): void {
    this.questionsNumber.update((current) => Math.max(MIN_AI_QUESTIONS, current - 1));
  }

  incrementNumberOfAnswers(): void {
    this.numberOfAnswers.update((current) => Math.min(MAX_AI_ANSWERS, current + 1));
  }

  decrementNumberOfAnswers(): void {
    this.numberOfAnswers.update((current) => Math.max(MIN_AI_ANSWERS, current - 1));
  }

  onGenerateClick(): void {
    if (!this.hasContent()) return;
    this.generate.emit({
      source: this.source(),
      questionsNumber: this.mode() === 'question' ? MIN_AI_QUESTIONS : this.questionsNumber(),
      numberOfAnswers: this.numberOfAnswers(),
      competencyIds: this.selectedCompetencies().map((competency) => competency._id),
      answerType: this.mode() === 'question' ? this.answerType() : 'multipleChoice',
    });
  }
}
